import { Button, Classes, Colors, Dialog, Icon, NonIdealState } from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import { FilePondFile } from "filepond";
import { FormikProps } from "formik";
import moment from "moment";
import {
  ButtonContainer,
  FGCheckboxInput,
  FGCustomInput,
  FGCustomPanel,
  FGListen,
  FGNumberInput,
  FGSelectInput,
  FGTextInput,
  FieldGroup,
  IFGContext,
  showError,
  showSuccess,
  useApiEffect,
} from "nsitools-react";
import * as React from "react";
import { FilePond } from "react-filepond";
import styled from "styled-components";
import * as Yup from "yup";

import {
  BroodRequestApi,
  BroodRequestDto,
  CalendarYearApi,
  CountryApi,
  CoupleInbreedingApi,
  DogApi,
  EDogSex,
  ELabStatus,
  EPlatform,
  ERequestState,
  ErrorDetails,
  SaveDogBroodRequestTransientDto,
} from "../../../../api";
import { FGCountrySelect, FGSRSHCheckboxInput, FGSRSHDateMaskInput } from "../../../../components";
import { useAuth } from "../../../../contexts";
import { useApiService, useApplication } from "../../../../hooks";
import { useReferential } from "../../../../hooks/useReferential";
import { ETLCodes } from "../../../../locales";
import { BroodRequestPotentialFatherSelector } from "../../../../pages/request/broodRequest/item/BroodRequestPotentialFatherSelector";
import { PuppyCard } from "../../../../pages/request/broodRequest/item/PuppyCard";
import { DrupalStepperButtonBloc } from "../../../common";
import { DrupalFormGenerator } from "../../../common/DrupalFormGenerator";
import { DrupalFieldSet } from "../../../common/DurpalFieldSet";
import { DrupalBroodRequestNewFatherForm } from "./DrupalBroodRequestNewFatherForm";
import { toUpperCamelCase } from "../../../../utils";
import { isBrcs } from "../../../DrupalApp";
import { DogInbreedingVerificationTab } from "../../../../pages/request/broodRequest/item/DogInbreedingVerificationTab";
import { useQuery } from "react-query";

export interface IDrupalBroodRequestFormProps {
  broodRequestId: number;
  stepNumber: number;
  initialdata: any;
  onStepChange: (newStep: number) => void;
  save: (data?: any) => Promise<any> | any;
  loading: boolean;
  refresh: any;
  showStepper?: boolean;
  setAdnProfile: React.Dispatch<React.SetStateAction<FilePondFile>>;
  setPedigreeFile: React.Dispatch<React.SetStateAction<FilePondFile>>;
  setpuppyPictures: React.Dispatch<React.SetStateAction<FilePondFile[]>>;
  setFatherAdnProfile: React.Dispatch<React.SetStateAction<FilePondFile>>;
  setFatherPedigreeFile: React.Dispatch<React.SetStateAction<FilePondFile>>;
  fatherId: number;
  setFatherId: React.Dispatch<React.SetStateAction<number>>;
  motherId: number;
  setMotherId: React.Dispatch<React.SetStateAction<number>>;
}

const StyledFGCustomPanel = styled(FGCustomPanel)`
  margin-top: 2rem;
`;

const StyledInfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  border: 2px solid #3f76f9;
  background-color: #c0e4fc;
  margin: 0.5em 5em;
  padding: 0.5rem;
  text-align: center;
  border-radius: 10px;
`;
const StyledErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  border: 2px solid #ac2f33;
  background-color: #ffebee;
  margin: 0.5em 5em;
  padding: 0.5rem;
  text-align: center;
  border-radius: 10px;
`;

const Container = styled.div``;

const StyledImage = styled(Icon)`
  color: #1e90ff;
  size: 9px;
  margin-right: 0.5rem;
`;
const StyledErrorImage = styled(Icon)`
  color: #ac2f33;
  size: 9px;
  margin-right: 0.5rem;
`;

export const DrupalBroodRequestForm: React.FunctionComponent<IDrupalBroodRequestFormProps> = ({
  showStepper = true,
  broodRequestId,
  onStepChange,
  stepNumber,
  save,
  loading,
  initialdata,
  refresh,
  setAdnProfile,
  setPedigreeFile,
  setFatherAdnProfile,
  setFatherPedigreeFile,
  fatherId,
  setFatherId,
  motherId,
  setMotherId,
  setpuppyPictures,
}) => {
  const { t, tUnsafe, validators } = useApplication();
  const formikRef = React.useRef<FormikProps<BroodRequestDto>>();
  const { user } = useAuth();
  const dogApi = useApiService(DogApi);
  const countryApi = useApiService(CountryApi);
  const api = useApiService(BroodRequestApi);
  const inbreedingApi = useApiService(CoupleInbreedingApi);
  const calendarApi = useApiService(CalendarYearApi);
  const [savePuppiesLoading, setsavePuppiesLoading] = React.useState(false);
  const data = React.useMemo(() => initialdata, [initialdata]);

  const [fatherInbreedingDialogOpen, setfatherInbreedingDialogOpen] = React.useState(false);

  React.useEffect(() => {
    setpotentialFatherIds(data.potentialFatherIds);
  }, [data.potentialFatherIds]);

  const [Pedigree] = useReferential((c) => c.getPuppyPedigrees(), true, [initialdata]);
  const [currentStep, setCurrentStep] = React.useState(stepNumber);
  const [postalCode, setpostalCode] = React.useState("");
  const [visible, setvisible] = React.useState<boolean>();
  const [potentialFatherIds, setpotentialFatherIds] = React.useState<number[]>();
  const [postalCodes, pcloading] = useReferential((a) => a.getStringifiedBelgianPostalCodes(), true, []);
  const [currentInbreedingError, setcurrentInbreedingError] = React.useState<ETLCodes>(undefined);
  const [localities, locloading] = useReferential(
    (a) => a.getStringifiedBelgianLocalityFromPostalCode(postalCode ?? ""),
    false,
    [postalCode]
  );
  const [Breed] = useReferential(
    (c) =>
      initialdata?.dogBroodRequestGetDto?.motherBreedId
        ? c.getBreedVarietiesWithCurrentBreed(initialdata?.dogBroodRequestGetDto?.motherBreedId)
        : null,
    true,
    [initialdata]
  );

  const [mothers, mloading] = useReferential(
    (a) => a.getOwnedDogs(user.userId, initialdata?.motherId ? +initialdata?.motherId : 0, EDogSex.Female, true, null),
    true,
    []
  );

  const saveForm = React.useCallback(
    async (formikData, override?: boolean) => {
      console.log("saving..");
      if (currentStep === 1 || override) {
        await save(formikData);
        refresh();
      }
    },
    [currentStep, refresh, save]
  );

  const savePuppies = React.useCallback(
    async (formikData) => {
      setsavePuppiesLoading(true);
      console.log("inside save func..");
      try {
        var res = await api.saveDogBroodRequestTransient(
          SaveDogBroodRequestTransientDto.fromJS({
            puppies: formikData.dogBroodRequestGetDto.puppies.map((p) => {
              return { ...p, name: toUpperCamelCase(p.name) };
            }),
            definitiveSave: formikData.definitiveSave,
          })
        );
        showSuccess(t(ETLCodes.SaveSuccess));
        setsavePuppiesLoading(false);
        refresh();
        return res;
      } catch (err) {
        const details: ErrorDetails = JSON.parse(err.response);
        showError(tUnsafe(`BackendError_${details.errorCode}`));
      }

      setsavePuppiesLoading(false);
    },
    [api, refresh, t, tUnsafe]
  );

  const birthYear = React.useMemo(
    () => (initialdata?.birthDate ? moment(initialdata.birthDate).year() : null),
    [initialdata?.birthDate]
  );

  const [yearLetter] = useApiEffect(() => {
    if (birthYear) {
      return calendarApi.getCalendarYearLetter(birthYear);
    }
  }, [birthYear]);

  const { data: isCoupleInbreedingComplete, refetch } = useQuery(
    ["couple-inbreeding", motherId, potentialFatherIds],
    async () => {
      if (motherId != undefined && motherId > 0 && potentialFatherIds.length > 0) {
        let result = await inbreedingApi.isCoupleInbreedingComplete(motherId, potentialFatherIds);
        if (result) setcurrentInbreedingError(undefined);
        return result;
      }
    }
  );

  const formSchema = React.useMemo(() => {
    return Yup.object().shape({
      motherId: Yup.number()
        .nullable()
        .required(t(ETLCodes.Required))
        .when("status", {
          is: ERequestState.REQUESTED,
          then: Yup.number()
            .test("dog-deceased", t(ETLCodes.DogIsDeceased), validators.dog.dogAlive)
            .test("is-not-excluded", t(ETLCodes.DogIsExcluded), validators.dog.dogNotExcluded),
        }),
      protrusionDate: Yup.date()
        .nullable()
        .required(t(ETLCodes.Required))
        .test(
          "requestDate-diff",
          t(ETLCodes.ThereMustBe44DaysMaximumBetweenProtrusionAndRequestDate),
          async function (value) {
            if (value) {
              const diff = moment(value).diff(moment(this.parent.request.requestDate), "days");
              return diff <= 44;
            }
            return true;
          }
        ),
      potentialFatherIds: Yup.array()
        .min(1, t(ETLCodes.AtLeastOnePotentialFatherMustBeChosen))
        .test("has_inbreeding_checked", t(ETLCodes.ErrorNotAllFatherHaveInbreeding), async function () {
          if (!isBrcs) return true;
          if (!isCoupleInbreedingComplete) setcurrentInbreedingError(ETLCodes.ErrorNotAllFatherHaveInbreeding);
          else setcurrentInbreedingError(undefined);
          return isCoupleInbreedingComplete;
        }),
      street: Yup.string()
        .nullable()
        .test("is-required", t(ETLCodes.Required), async function (value) {
          if (this.parent.otherAddress) {
            return !!value;
          }
          return true;
        }),
      number: Yup.string()
        .nullable()
        .test("is-required", t(ETLCodes.Required), async function (value) {
          if (this.parent.otherAddress) {
            return !!value;
          }
          return true;
        }),
      city: Yup.string()
        .nullable()
        .test("is-required", t(ETLCodes.Required), async function (value) {
          if (this.parent.otherAddress) {
            return !!value;
          }
          return true;
        }),
      postalCode: Yup.string()
        .nullable()
        .test("is-required", t(ETLCodes.Required), async function (value) {
          if (this.parent.otherAddress) {
            return !!value;
          }
          return true;
        }),
      countryId: Yup.number()
        .nullable()
        .test("is-not-begium", t(ETLCodes.IsNotBelgium), async function (value) {
          if (this.parent.otherAddress && !!value) {
            const result = await countryApi.isBelgian(value);
            return result.value;
          }
          return true;
        }),
      birthDate: Yup.date()
        .nullable()
        .test("is-required", t(ETLCodes.Required), async function (value) {
          if (!!this.parent.birthDate || initialdata?.request?.status === ERequestState.GESTATION) {
            return value !== null && value !== undefined;
          }
          return true;
        })
        .test(
          "protusionDate-diff",
          t(ETLCodes.ProtusionDateAndBirthDateMustBeBetween54And68Days),
          async function (value) {
            if ((!!this.parent.birthDate || initialdata?.request?.status === ERequestState.GESTATION) && value) {
              const diff = moment(value).diff(moment(this.parent.protrusionDate), "days");
              return diff >= 54 && diff <= 68;
            }
            return true;
          }
        ),
      numberOfPuppies: Yup.number()
        .test("is-required", t(ETLCodes.Required), async function (value) {
          if (!!this.parent.birthDate || initialdata?.request?.status === ERequestState.GESTATION) {
            return value !== null && value !== undefined && value > 0;
          }
          return true;
        })
        .test("number-check", t(ETLCodes.NumberMustBeBetween1And20), async function (value) {
          if (value !== null && value !== undefined) {
            return value >= 1 && value <= 20;
          }
          return true;
        }),
    });
  }, [
    initialdata?.request?.status,
    validators.dog.dogAlive,
    validators.dog.dogNotExcluded,
    isCoupleInbreedingComplete,
  ]);

  React.useEffect(() => {
    formikRef.current.validateForm();
  }, [isCoupleInbreedingComplete]);

  const completePuppy = React.useCallback((parent: any, fieldName: string) => {
    console.log(
      `KnownValues:\nName:${parent.name}\nOwners length:${parent.owners?.length}\nColorId=${parent.colorId}\nBreedId:${parent.breedId}`
    );
    if (parent.Deceased) {
      return true;
    }
    if (
      parent.name !== null &&
      parent.colorId !== null &&
      parent.breedId !== null &&
      !!parent.owners &&
      parent.owners?.length > 0
    ) {
      return true;
    }
    if (
      (parent.name === null || parent.name === "") &&
      parent.colorId === null &&
      !!parent.owners &&
      parent.owners?.length === 0
    ) {
      return true;
    }
    if (!parent[fieldName]) {
      return false;
    } else if (fieldName === "owners" && parent.owners?.length === 0) {
      return false;
    }
    return true;
  }, []);

  const puppiesFormSchema = React.useMemo(() => {
    return Yup.object().shape({
      dogBroodRequestGetDto: Yup.object().shape({
        puppies: Yup.array().of(
          Yup.object().shape({
            name: Yup.string()
              .nullable()
              .test("correct-year", t(ETLCodes.NameDoesNotStartWithCalendarLetter), async function (value) {
                if (value !== null && value !== undefined && value !== "") {
                  console.log("Value of name is ", value);
                  if (!this.parent.deceased && this.parent?.labStatus === ELabStatus.AnalysisDone) {
                    return value.charAt(0) === yearLetter;
                  }
                }
                return true;
              })
              .test("name-unicity", t(ETLCodes.NameAlreadyTaken), async function (value) {
                if (!this.parent.kennelId || !value) {
                  return true;
                }
                var res = await dogApi.getDogUnicityQuery(this.parent.name, this.parent.kennelId);

                return res.value;
              })
              .test("complete-puppy", t(ETLCodes.PuppyInformationIncomplete), async function () {
                return completePuppy(this.parent, "name");
              }),
            puppyOwnerEmail: Yup.string()
              .nullable()
              .test("is-correct-email", t(ETLCodes.InvalidEmail), async function (value) {
                if (value !== null && value !== undefined && value !== "") {
                  const regexp = /(.*)[@]{1}(.*)[.]{1}([a-z]*)/g;
                  const matching = regexp.test(value);
                  return matching;
                }
                return true;
              }),
            colorId: Yup.number()
              .nullable()
              .test("complete-puppy", t(ETLCodes.PuppyInformationIncomplete), async function () {
                return completePuppy(this.parent, "colorId");
              }),
            owners: Yup.array().test(
              "complete-puppy-owners",
              t(ETLCodes.PuppyInformationIncomplete),
              async function (value) {
                return completePuppy(this.parent, "owners");
              }
            ),
            countryPedigreePrefixId: Yup.number()
              .nullable()
              .test("not-nullable", t(ETLCodes.Required), async function (value) {
                if (!this.parent.deceased) {
                  return value !== null && value !== undefined;
                }
                return true;
              }),
          })
        ),
      }),
    });
  }, [completePuppy, t, yearLetter]);

  const [hasAdnProfile, setHasAdnProfile] = React.useState(false);

  React.useEffect(() => {
    if (potentialFatherIds && potentialFatherIds?.length > 0) {
      dogApi.hasAdnProfile(potentialFatherIds[0], true).then((b) => {
        setFatherId(potentialFatherIds[0]);
        setHasAdnProfile(b.value);
      });
    } else {
      setFatherId(null);
      setFatherAdnProfile(null);
      setFatherPedigreeFile(null);
    }
  }, [dogApi, potentialFatherIds, setFatherAdnProfile, setFatherId, setFatherPedigreeFile]);

  const [formWidth, setformWidth] = React.useState<number>();

  const [loadingResendSample, setloadingResendSample] = React.useState(false);

  const resendPedigreeSample = React.useCallback(async () => {
    setloadingResendSample(true);
    try {
      await api.generatePedigreeSample(data?.requestId);
      showSuccess(t(ETLCodes.Success));
    } catch {
      showError(t(ETLCodes.Error));
    } finally {
      setloadingResendSample(false);
    }
  }, [api, data, t]);

  const allPuppiesAnalyzed = React.useMemo(
    () => data?.dogBroodRequestGetDto?.puppies?.every((c) => c.labStatus === ELabStatus.AnalysisDone),
    [data]
  );

  const InnerOnStepChange = React.useCallback(
    async (newStep: number) => {
      if (isBrcs) {
        if (!isCoupleInbreedingComplete) {
          setcurrentInbreedingError(ETLCodes.ErrorNotAllFatherHaveInbreeding);
          return;
        }
        setcurrentInbreedingError(undefined);
      }
      setCurrentStep(newStep);
      onStepChange(newStep);
    },
    [motherId, potentialFatherIds, isCoupleInbreedingComplete]
  );

  const onCloseInbreedingDialog = React.useCallback(async () => {
    setfatherInbreedingDialogOpen(false);
    await refetch();
  }, []);

  return (
    initialdata && (
      <div>
        <DrupalFormGenerator
          initialValues={data}
          editMode={
            (currentStep === 0 && initialdata?.request?.status === ERequestState.REQUESTED) ||
            initialdata?.request?.status === ERequestState.DOG_VALIDATION ||
            initialdata?.request?.status === ERequestState.LAB_WORK ||
            initialdata?.request?.status === ERequestState.GESTATION
          }
          onSubmit={() => {}}
          saving={false}
          validationSchema={formSchema}
          validateOnChange={true}
          loading={loading}
          hideButtons
          formikInnerRef={(f) => (formikRef.current = f)}
          onFormWidthChanged={setformWidth}
        >
          <FGListen
            field="postalCode"
            onChanged={(value) => {
              setpostalCode(value);
            }}
          />
          <FGListen
            field="motherId"
            onChanged={(value) => {
              if (motherId !== value) setMotherId(value);
            }}
          />
          <FGListen
            field="otherAddress"
            onChanged={async (value, formik) => {
              if (value === true) {
                const belgiumId = await countryApi.getBelgianId();
                formik.setFieldValue("countryId", belgiumId);
              } else {
                formik.values.countryId = null;
              }
            }}
          />
          <FGListen
            field="otherAddress"
            onChanged={async (value, formik) => {
              if (value === true) {
                const belgiumId = await countryApi.getBelgianId();
                formik.setFieldValue("countryId", belgiumId);
              } else {
                formik.values.countryId = null;
              }
            }}
          />
          <FGSelectInput
            name="motherId"
            label={t(ETLCodes.Mother)}
            items={mothers}
            loading={mloading}
            readonly={initialdata?.request?.status !== ERequestState.REQUESTED}
          />
          <FGSRSHDateMaskInput
            name="protrusionDate"
            label={t(ETLCodes.ProtrusionDate)}
            readonly={initialdata?.request?.status !== ERequestState.REQUESTED}
          />
          <FGNumberInput name="price" label={t(ETLCodes.PuppyPrice)} />
          <FGCheckboxInput
            name="progenusQuickDelivery"
            label={t(ETLCodes.ProgenusQuickDelivery)}
            readonly={initialdata?.request?.status !== ERequestState.REQUESTED}
          />
          <FGCheckboxInput
            name="isRegisteredLetter"
            label={t(ETLCodes.IsRegisteredLetter)}
            readonly={initialdata?.request?.status !== ERequestState.REQUESTED}
            helperText={t(ETLCodes.IfNotRegisteredLetterSrshIsNotResponsibleForAnyLostDocument)}
          />

          <FieldGroup columns={2}>
            <FGSRSHCheckboxInput
              label={t(ETLCodes.OtherAddress)}
              name="otherAddress"
              disabled={initialdata?.request?.status !== ERequestState.REQUESTED}
            />
          </FieldGroup>
          <FieldGroup columns={2}>
            <FGTextInput
              label={t(ETLCodes.Street)}
              name="street"
              readonly={initialdata?.request?.status !== ERequestState.REQUESTED}
              visible={(ctx) => ctx.formik.values.otherAddress}
              requiredMark={true}
            />
            <FGTextInput
              label={t(ETLCodes.Number)}
              name="number"
              readonly={
                initialdata?.request?.status !== ERequestState.REQUESTED || initialdata?.numberOfPuppiesConfirmed
              }
              visible={(ctx) => ctx.formik.values.otherAddress}
              requiredMark={true}
            />
          </FieldGroup>
          <FieldGroup columns={2}>
            <FGCustomInput label={t(ETLCodes.Affix)} visible={broodRequestId > 0}>
              {({ formik }) => formik.values.affix}
            </FGCustomInput>
          </FieldGroup>
          <FieldGroup columns={2}>
            <FGSelectInput
              name="postalCode"
              items={postalCodes}
              loading={pcloading}
              label={t(ETLCodes.PostalCode)}
              readonly={initialdata?.request?.status !== ERequestState.REQUESTED}
              visible={(ctx) => ctx.formik.values.otherAddress}
              requiredMark={true}
            />
            <FGSelectInput
              name="city"
              items={localities}
              loading={locloading}
              label={t(ETLCodes.City)}
              readonly={initialdata?.request?.status !== ERequestState.REQUESTED}
              visible={(ctx) => ctx.formik.values.otherAddress}
              requiredMark={true}
            />
          </FieldGroup>
          <FieldGroup columns={2}>
            <FGCountrySelect
              name="countryId"
              label={t(ETLCodes.Country)}
              visible={(ctx) => ctx.formik.values.otherAddress}
              readonly
            />
          </FieldGroup>
          {currentStep === 0 && initialdata.request.status === ERequestState.REQUESTED ? (
            <div>
              <FGCustomInput name="pedigreeFile" label={t(ETLCodes.Pedigree)}>
                {(ctx) => (
                  <FilePond
                    labelIdle={t(ETLCodes.FilePondDragOrSelect)}
                    onupdatefiles={(f) => {
                      ctx.formik.setFieldValue("pedigreeFile", f.length > 0 ? f[0] : null);
                      setPedigreeFile(f.length > 0 ? f[0] : null);
                    }}
                    disabled={currentStep !== 0}
                  />
                )}
              </FGCustomInput>
              <FGCustomInput name="adnProfile" label={t(ETLCodes.ADNProfile)}>
                {(ctx) => (
                  <FilePond
                    labelIdle={t(ETLCodes.FilePondDragOrSelect)}
                    onupdatefiles={(f) => {
                      ctx.formik.setFieldValue("adnProfile", f.length > 0 ? f[0] : null);
                      setAdnProfile(f.length > 0 ? f[0] : null);
                    }}
                    disabled={currentStep !== 0}
                  />
                )}
              </FGCustomInput>
            </div>
          ) : null}
          <DrupalFieldSet title={t(ETLCodes.PotentialFathers)}>
            {motherId ? (
              <div>
                {formikRef?.current?.values.potentialFatherIds?.length < 1 && (
                  <StyledErrorContainer>
                    <div>
                      <StyledErrorImage icon="warning-sign" />
                      {t(ETLCodes.NotFatherSelected)}
                    </div>
                  </StyledErrorContainer>
                )}
                <BroodRequestPotentialFatherSelector
                  clickableDogs={false}
                  linkedIds={potentialFatherIds || []}
                  onLinkedIdsChanged={(ids) => {
                    setpotentialFatherIds(ids);
                    formikRef.current?.setFieldValue("potentialFatherIds", ids, true);
                  }}
                  motherId={formikRef.current?.values?.motherId}
                  editMode={
                    currentStep === 0 &&
                    !initialdata?.request?.isBlocked &&
                    initialdata?.request?.status === ERequestState.REQUESTED
                  }
                  loading={false}
                  showAllButtons={false}
                  formWidth={formWidth}
                />

                {currentStep === 0 && initialdata?.request?.status === ERequestState.REQUESTED ? (
                  <Container>
                    <StyledInfoContainer>
                      <div>
                        <StyledImage icon="info-sign" />
                        {t(ETLCodes.FatherNotFoundClickAdd)}
                      </div>
                    </StyledInfoContainer>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <Button
                        style={{
                          width: formWidth < 1200 ? "30%" : "15%",
                        }}
                        icon="add"
                        text={t(ETLCodes.DrupalCreateNewDog)}
                        onClick={() => setvisible(!visible)}
                      />
                    </div>
                  </Container>
                ) : null}
              </div>
            ) : (
              <NonIdealState icon="resolve" description={t(ETLCodes.PleaseSelectDogToViewPossibleFathers)} />
            )}
            {fatherId && currentStep === 0 && initialdata.request.status === ERequestState.REQUESTED && (
              <FieldGroup>
                <FGCustomPanel>
                  {(ctx) => (
                    <StyledInfoContainer>
                      <div>
                        <StyledImage icon="info-sign" />
                        {t(ETLCodes.IfDogNotOwnedContactSrsh)}
                      </div>
                    </StyledInfoContainer>
                  )}
                </FGCustomPanel>
                {isBrcs && currentStep === 0 && (
                  <>
                    {currentInbreedingError && (
                      <StyledErrorContainer>
                        <div>
                          <StyledErrorImage icon="warning-sign" />
                          {t(currentInbreedingError)}
                        </div>
                      </StyledErrorContainer>
                    )}
                    <div style={{ display: "flex", justifyContent: "center", width: "100%", marginBottom: "0.5rem" }}>
                      <Button
                        text={t(ETLCodes.ConfigureFatherInbreeding)}
                        icon="diagram-tree"
                        intent="primary"
                        onClick={() => setfatherInbreedingDialogOpen(true)}
                      />
                    </div>
                  </>
                )}
                {!hasAdnProfile && (
                  <FGCustomInput name="fatherAdnProfile" label={t(ETLCodes.ADNProfile)}>
                    {(ctx) => (
                      <FilePond
                        labelIdle={t(ETLCodes.FilePondDragOrSelect)}
                        onupdatefiles={(f) => {
                          ctx.formik.setFieldValue("fatherAdnProfile", f.length > 0 ? f[0] : null);
                          setFatherAdnProfile(f.length > 0 ? f[0] : null);
                        }}
                      />
                    )}
                  </FGCustomInput>
                )}
                <FGCustomInput name="fatherPedigreeFile" label={t(ETLCodes.Pedigree)}>
                  {(ctx) => (
                    <FilePond
                      labelIdle={t(ETLCodes.FilePondDragOrSelect)}
                      onupdatefiles={(f) => {
                        ctx.formik.setFieldValue("fatherPedigreeFile", f.length > 0 ? f[0] : null);
                        setFatherPedigreeFile(f.length > 0 ? f[0] : null);
                      }}
                    />
                  )}
                </FGCustomInput>
              </FieldGroup>
            )}
          </DrupalFieldSet>

          <FGCustomPanel>
            {(ctx) =>
              ctx?.formik?.values?.potentialFatherIds?.length > 3 && (
                <div style={{ color: "RED", margin: "0 0 0 14em" }}>
                  <i className="fas fa-exclamation-triangle" />
                  {t(ETLCodes.PotentialFatherCostWarning)}
                </div>
              )
            }
          </FGCustomPanel>
          <DrupalFieldSet title={t(ETLCodes.PuppiesBirth)} divid={true}>
            <FGCheckboxInput name="bornPuppies" label={t(ETLCodes.PuppiesAreBorn)} />
            <FGSRSHDateMaskInput
              label={t(ETLCodes.BirthDate)}
              name="birthDate"
              requiredMark
              visible={(ctx: IFGContext<any>) => {
                return !!ctx.formik.values?.bornPuppies;
              }}
              readonly={initialdata?.request?.status === ERequestState.LAB_WORK}
            />
            <FGNumberInput
              label={t(ETLCodes.NumberOfPuppies)}
              name="numberOfPuppies"
              requiredMark
              visible={(ctx: IFGContext<any>) => {
                return !!ctx.formik.values?.bornPuppies;
              }}
              readonly={initialdata?.request?.status === ERequestState.LAB_WORK}
            />
            <FGCheckboxInput
              label={t(ETLCodes.NumberOfPuppiesConfirmed)}
              name="numberOfPuppiesConfirmed"
              visible={(ctx: IFGContext<any>) => {
                return !!ctx.formik.values?.bornPuppies;
              }}
              readonly={initialdata?.request?.status === ERequestState.LAB_WORK}
            />

            {isBrcs && (
              <FGCheckboxInput
                name="wantSrshPedigree"
                label={t(ETLCodes.SrshPedigreeRequestedWarningWillBeBilled)}
                visible={(ctx: IFGContext<any>) => {
                  return !!ctx.formik.values?.bornPuppies;
                }}
                readonly={initialdata?.request?.status === ERequestState.LAB_WORK}
              />
            )}

            <FGCustomInput name="puppyPictures">
              {(ctx) =>
                ctx.formik.values?.bornPuppies ? (
                  <FilePond
                    labelIdle={t(ETLCodes.FilePondDragOrSelectMax3PuppiesPicture)}
                    onupdatefiles={(f) => {
                      ctx.formik.setFieldValue("puppyPictures", f.length > 0 ? f : null);
                      setpuppyPictures(f.length > 0 ? f : null);
                    }}
                    allowMultiple
                    maxFiles={3}
                  />
                ) : null
              }
            </FGCustomInput>
          </DrupalFieldSet>
          {showStepper ? (
            <FGCustomPanel>
              {(ctx) => {
                return initialdata?.request?.status === ERequestState.DOG_VALIDATION ||
                  initialdata?.request?.status === ERequestState.GESTATION ? (
                  <DrupalStepperButtonBloc
                    backOnly={false}
                    disabled={!ctx.formik.dirty}
                    loading={loading}
                    onStepChange={() => {}}
                    stepNumber={currentStep}
                    nextButtonText={t(ETLCodes.General_Save)}
                    nextButtonIcon={IconNames.FLOPPY_DISK}
                    onNextFn={() => {
                      saveForm(ctx?.formik?.values, true);
                    }}
                    formik={ctx.formik}
                  />
                ) : (
                  <DrupalStepperButtonBloc
                    backOnly={
                      initialdata?.request?.status !== ERequestState.REQUESTED &&
                      initialdata?.request?.status !== ERequestState.LAB_WORK
                    }
                    disabled={ctx?.formik?.values === initialdata}
                    loading={loading}
                    onStepChange={InnerOnStepChange}
                    stepNumber={currentStep}
                    onNextFn={() => saveForm(ctx?.formik?.values)}
                    formik={ctx.formik}
                  />
                );
              }}
            </FGCustomPanel>
          ) : null}
        </DrupalFormGenerator>

        {(initialdata?.request?.status === ERequestState.LAB_WORK ||
          initialdata?.request?.status === ERequestState.EXECUTED ||
          initialdata?.request?.status === ERequestState.EXECUTED_AWAITING_DOCUMENTS) && (
          <DrupalFormGenerator
            initialValues={initialdata}
            editMode={initialdata?.request?.status === ERequestState.LAB_WORK}
            onSubmit={async (d: any) => {
              console.log("saving pups");
              console.log(d);
              await savePuppies(d);
            }}
            saving={savePuppiesLoading}
            validationSchema={puppiesFormSchema}
            loading={loading}
            hideButtons={initialdata?.request?.status !== ERequestState.LAB_WORK}
          >
            <DrupalFieldSet
              title={t(ETLCodes.Puppies)}
              rightElement={
                <ButtonContainer>
                  {data?.dogBroodRequestGetDto?.puppies?.length > 0 && allPuppiesAnalyzed && (
                    <Button
                      loading={loadingResendSample}
                      onClick={resendPedigreeSample}
                      text={t(ETLCodes.ResendPedigreeSampleDrupal)}
                      intent="primary"
                      icon="envelope"
                      minimal
                    />
                  )}
                </ButtonContainer>
              }
            >
              <StyledFGCustomPanel>
                {(ctx) =>
                  ctx?.formik?.values.dogBroodRequestGetDto.puppies?.map((puppy, index) => (
                    <PuppyCard
                      key={index}
                      index={index}
                      puppy={puppy}
                      breeds={Breed}
                      pedigrees={Pedigree}
                      refresh={refresh}
                      platform={EPlatform.PORTAL}
                      yearLetter={yearLetter}
                      tooSmall={formWidth < 1200}
                    />
                  ))
                }
              </StyledFGCustomPanel>
            </DrupalFieldSet>
            {data.request.status === ERequestState.LAB_WORK && (
              <FGCheckboxInput name="definitiveSave" label={t(ETLCodes.DefinitiveSave)} />
            )}
          </DrupalFormGenerator>
        )}

        <Dialog
          title={t(ETLCodes.CreatingFather)}
          isOpen={visible}
          onClose={() => setvisible(false)}
          canEscapeKeyClose={true}
          canOutsideClickClose={true}
          isCloseButtonShown={true}
          className={Classes.DIALOG}
          style={{
            width: formWidth > 1200 ? 1200 : formWidth,
            background: Colors.WHITE,
          }}
        >
          <div style={{ margin: "1rem" }}>
            <DrupalBroodRequestNewFatherForm
              setVisible={setvisible}
              selectedIds={potentialFatherIds ?? []}
              setSelectedIds={setpotentialFatherIds}
              motherId={formikRef.current?.values.motherId}
            />
          </div>
        </Dialog>

        <Dialog
          title={t(ETLCodes.FatherInbreeding)}
          isOpen={fatherInbreedingDialogOpen}
          onClose={() => setfatherInbreedingDialogOpen(false)}
          canEscapeKeyClose={true}
          canOutsideClickClose={true}
          isCloseButtonShown={true}
          className={Classes.DIALOG}
          style={{
            width: formWidth > 1200 ? 1200 : formWidth,
            background: Colors.WHITE,
          }}
        >
          <div style={{ margin: "1rem" }}>
            <DogInbreedingVerificationTab
              motherId={motherId}
              fatherIds={potentialFatherIds}
              redirectOnSave={false}
              onSaved={onCloseInbreedingDialog}
              isDrupal={true}
            />
          </div>
        </Dialog>
      </div>
    )
  );
};
